/**
 * 점주 콘솔 v2 전용 MUI 테마.
 *
 * OwnerConsoleApp 하위에서만 ThemeProvider로 감싸 적용한다.
 * 기존 관리자 App.js의 테마에는 영향을 주지 않는다 (중첩 ThemeProvider가 하위 트리만 오버라이드).
 */
import { createTheme } from '@mui/material';
import { colors, layout, shadows, radius, typography } from './tokens';

export const ownerConsoleTheme = createTheme({
  typography: {
    fontFamily: typography.fontFamily,
    allVariants: {
      letterSpacing: 0,
      wordBreak: 'keep-all',
      overflowWrap: 'anywhere',
    },
    h4: { fontSize: 24, lineHeight: 1.15, fontWeight: 800, color: colors.text.primary },
    h5: { fontSize: 22, lineHeight: 1.2, fontWeight: 800, color: colors.text.primary },
    h6: { fontSize: 14.5, lineHeight: 1.45, fontWeight: 700, color: colors.text.primary },
    subtitle2: { fontSize: 14.5, lineHeight: 1.45, fontWeight: 700, color: colors.text.primary },
    body1: { fontSize: 13.5, lineHeight: 1.5 },
    body2: { fontSize: 13, lineHeight: 1.5 },
    caption: { fontSize: 12.5, lineHeight: 1.5, color: colors.text.muted },
    button: { fontSize: 13, lineHeight: 1.35, fontWeight: 700, textTransform: 'none' },
  },
  palette: {
    primary: {
      main: colors.brand.primary,
      dark: colors.brand.primaryHover,
      contrastText: colors.text.inverse,
    },
    secondary: {
      main: colors.navy,
      dark: colors.text.primary,
      contrastText: colors.text.inverse,
    },
    success: { main: colors.status.success.fg, light: colors.status.success.bg },
    warning: { main: colors.status.warning.fg, light: colors.status.warning.bg },
    error: { main: colors.status.danger.fg, light: colors.status.danger.bg },
    background: {
      default: colors.background,
      paper: colors.surface,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.muted,
    },
    divider: colors.border.soft,
  },
  shape: { borderRadius: radius.md },
  components: {
    // 카드 — hairline border를 기본 구분선으로 사용한다.
    MuiPaper: {
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderColor: colors.border.muted,
          boxShadow: shadows.card,
          borderRadius: radius.md,
          minWidth: 0,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 34,
          borderRadius: radius.button,
          textTransform: 'none',
          boxShadow: 'none',
          padding: '8px 14px',
          lineHeight: 1.35,
          maxWidth: '100%',
          whiteSpace: 'normal',
          textAlign: 'center',
        },
        sizeSmall: {
          minHeight: 30,
          padding: '6px 11px',
          fontSize: 12.5,
        },
        contained: {
          '&:hover': { boxShadow: 'none' },
        },
        outlined: {
          borderColor: colors.border.muted,
          color: colors.text.primary,
          backgroundColor: colors.surface,
          '&:hover': {
            borderColor: colors.brand.primarySoftBorder,
            backgroundColor: colors.canvas.subtle,
          },
        },
        text: {
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: colors.canvas.inset,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: layout.controlHeight,
          borderRadius: radius.button,
          backgroundColor: colors.surface,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.muted,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.brand.primarySoftBorder,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.brand.primary,
            borderWidth: 1,
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.14)',
          },
          '&.MuiInputBase-sizeSmall': {
            minHeight: layout.compactControlHeight,
          },
        },
        input: {
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 12,
          paddingRight: 12,
        },
        inputSizeSmall: {
          paddingTop: 7,
          paddingBottom: 7,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: radius.sm,
          color: colors.text.secondary,
          '&.Mui-selected': {
            backgroundColor: colors.brand.primarySoft,
            color: colors.brand.primary,
            boxShadow: `inset 3px 0 0 ${colors.brand.primary}`,
            '&:hover': { backgroundColor: colors.brand.primarySoft },
          },
          '&:hover': {
            backgroundColor: colors.canvas.subtle,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          borderRadius: radius.chip,
          fontSize: 12,
          fontWeight: 700,
          maxWidth: '100%',
          '& .MuiChip-label': {
            paddingLeft: 9,
            paddingRight: 9,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        sizeSmall: {
          height: 22,
          fontSize: 11.5,
          '& .MuiChip-label': {
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${colors.border.muted}`,
          boxShadow: 'none',
          fontSize: 13,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '13px 14px',
          borderBottom: `1px solid ${colors.border.soft}`,
          fontSize: 13.5,
          verticalAlign: 'middle',
        },
        head: {
          backgroundColor: colors.canvas.subtle,
          color: colors.text.muted,
          fontSize: 12,
          fontWeight: 600,
          borderBottom: `1px solid ${colors.border.soft}`,
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: colors.canvas.subtle,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${colors.border.subtle}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${colors.border.muted}`,
          borderRadius: radius.lg,
          boxShadow: shadows.drawer,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          width: 440,
          maxWidth: '100vw',
          borderLeft: `1px solid ${colors.border.muted}`,
          boxShadow: shadows.drawer,
          [`@media (max-width:${layout.mobileBreakpoint - 1}px)`]: {
            width: '100vw',
            borderLeft: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          backgroundColor: colors.brand.primary,
        },
      },
    },
  },
});
